import { CopyButton } from '@/components/CopyButton'
import { cn } from '@/lib/utils'

interface CodeBlockProps {
  code: string
  /** Rendered as a caption strip above the code, e.g. `.mcp.json`. */
  filename?: string
  language?: string
  className?: string
  /** Caps the height and scrolls. Omit to let the block grow. */
  maxHeightClassName?: string
}

export function CodeBlock({
  code,
  filename,
  language,
  className,
  maxHeightClassName = 'max-h-[26rem]',
}: CodeBlockProps) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border border-border bg-[var(--surface)]',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3 border-b border-border bg-card px-3 py-1.5">
        <div className="flex min-w-0 items-center gap-2">
          {filename === undefined ? null : (
            <span className="truncate font-mono text-xs text-foreground">{filename}</span>
          )}
          {language === undefined ? null : (
            <span className="text-[0.6875rem] tracking-[0.08em] text-muted-foreground uppercase">
              {language}
            </span>
          )}
        </div>
        <CopyButton value={code} label={`Copy ${filename ?? 'code'}`} />
      </div>
      <pre
        className={cn(
          'overflow-auto px-4 py-3.5 font-mono text-[0.8125rem] leading-relaxed text-foreground',
          maxHeightClassName,
        )}
      >
        <code>{code}</code>
      </pre>
    </div>
  )
}

/** Single-line, inline-copyable identifier — MCP URLs, slugs, token prefixes. */
export function CopyableValue({
  value,
  label,
  className,
  muted = false,
}: {
  value: string
  label?: string
  className?: string
  muted?: boolean
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-1 rounded-md border border-border bg-[var(--surface)] py-1 pr-1 pl-2.5',
        className,
      )}
    >
      <span
        className={cn(
          'min-w-0 flex-1 truncate font-mono text-[0.8125rem]',
          muted ? 'text-muted-foreground' : 'text-foreground',
        )}
        title={value}
      >
        {value}
      </span>
      <CopyButton value={value} label={label ?? 'Copy value'} />
    </div>
  )
}
